import { memo, useCallback } from 'react'

import { TextField } from '@mui/material'
import { ExpManifestInsert } from '@nativewaves/nw-cloud-sdk/content'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'

import { useCloseHandler } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SnackPanelActions, ViewSpace } from '@shared/components/Snackbar'
import {
  selectSubmissionProgressSetters,
  SubmissionProgress,
  useSubmissionProgressStore,
} from '@shared/components/SubmissionProgress'
import { DialogFormContent } from '@shared/layouts'
import { SnackPanelActionButtonLink } from 'components/LinkedMUIComponents'
import { useProgramCreateMutation } from 'hooks/mutations/content'
import { expManifestsRoute } from 'pages/NWPlatform/Workspaces/Workspace/ExpManifests'
import { expManifestRoute } from 'pages/NWPlatform/Workspaces/Workspace/ExpManifests/ExpManifest'
import { workspaceQueryKeys } from 'services/queryKeys'

type FormStructure = ExpManifestInsert

const NewDialogRaw = () => {
  const { t } = useTranslation(['common', 'domain'])
  const { enqueueSnackbar } = useSnackbar()
  const handleClose = useCloseHandler()

  const { organizationId, environmentId, workspaceId } = expManifestsRoute.useParams()

  const { setCurrentStep, setError: setSubmissionError } = useSubmissionProgressStore(
    useShallow(selectSubmissionProgressSetters),
  )

  const workspaceQuery = useQuery(workspaceQueryKeys.detail({ workspaceId }))
  const { data: workspace } = workspaceQuery

  const expManifestCreateMutation = useProgramCreateMutation()
  const { mutate: createExpManifest } = expManifestCreateMutation

  const { handleSubmit, control } = useForm<FormStructure>({
    defaultValues: {
      repositoryId: workspace?.contentRepositoryId,
    },
  })

  const handleFormSubmit = useCallback(
    (expManifestInsert: FormStructure) => {
      setCurrentStep({
        value: 10,
        description: t('creating', { name: expManifestInsert.name }),
      })

      createExpManifest(
        { expManifestInsert },
        {
          onSuccess: (data) => {
            setCurrentStep({ value: 100, description: t('created', { name: data.name }) })

            setTimeout(() => {
              enqueueSnackbar(`${t('created', { name: data.name })}`, {
                description: `${t('domain:Workspace.ExpManifest.createdSuccessDescription')}`,
                variant: 'panel',
                persist: true,
                icon: 'success',
                Actions: ({ onClose }) => (
                  <SnackPanelActions>
                    <SnackPanelActionButtonLink
                      to={expManifestRoute.to}
                      params={{ organizationId, environmentId, workspaceId, expManifestId: data.id }}
                      onClick={() => onClose()}
                    >
                      <ViewSpace />
                    </SnackPanelActionButtonLink>
                  </SnackPanelActions>
                ),
              })

              handleClose()
            }, 700)
          },
          onError: (_, variables) => {
            setSubmissionError()
            setCurrentStep({
              value: 60,
              description: t('couldNotBeCreated', { name: variables.expManifestInsert.name }),
            })
          },
        },
      )
    },
    [
      createExpManifest,
      enqueueSnackbar,
      environmentId,
      handleClose,
      organizationId,
      setCurrentStep,
      setSubmissionError,
      t,
      workspaceId,
    ],
  )

  return (
    <DialogFormContent
      Illustration={null}
      onSubmit={handleSubmit(handleFormSubmit)}
      title={t('domain:Workspace.ExpManifest.add')}
      description={t('domain:Workspace.ExpManifest.addDescription')}
      SubmitProgressZone={
        <SubmissionProgress
          isSuccess={expManifestCreateMutation.isSuccess}
          isError={expManifestCreateMutation.isError}
        />
      }
    >
      <Controller
        name="name"
        control={control}
        render={({ field, fieldState: { invalid, error } }) => (
          <TextField
            {...field}
            autoFocus
            margin="dense"
            label={t('name')}
            type="text"
            fullWidth
            variant="standard"
            autoComplete="off"
            error={invalid}
            helperText={error?.message}
          />
        )}
      />
    </DialogFormContent>
  )
}

export const NewDialog = memo(NewDialogRaw)
